import type { Locale } from "./config";
import { enAuto, noAuto, zhHantAuto } from "./auto";

// Interface copy. English is the source of truth; every other locale falls
// back to the English string when a key is missing.
const enBase = {
  "nav.news": "News",
  "nav.weekly": "Weekly Brief",
  "nav.podcasts": "Podcasts",
  "nav.social": "Social",
  "nav.feed": "Feed",
  "nav.markets": "Markets",
  "nav.predictionMarkets": "Prediction Markets",
  "nav.intelligence": "Intelligence Pro",
  "nav.leaderboard": "Leaderboard",
  "nav.learn": "Learn",
  "nav.games": "Games",
  "nav.bookmarks": "Bookmarks",
  "nav.search": "Search",
  "nav.profile": "Profile",
  "nav.signIn": "Sign in",
  "nav.about": "About",
  "nav.editorialStandards": "Editorial standards",
  "nav.privacy": "Privacy Policy",
  "nav.terms": "Terms of Use",

  "common.readMore": "Read more",
  "common.loading": "Loading…",
  "common.tryAgain": "Try again",
  "common.share": "Share",
  "common.save": "Save",
  "common.language": "Language",
  "common.backHome": "Return to home",
  "common.minRead": "min read",
  "common.translated": "Machine translated",
  "common.translating": "Translating…",
  "common.readOriginal": "Read the English original",

  "home.title": "Byteline: Technology News and Market Intelligence",
  "home.subtitle":
    "Curated daily coverage of disruptive technology, from AI and semiconductors to space, robotics, energy and markets.",
  "home.latest": "Latest stories",
  "home.dailyRecap": "Daily recap",
  "home.topics": "Topics",

  "article.notFound": "Article not found",
  "article.relatedTitle": "Related stories",
  "article.faqTitle": "Frequently asked questions",
  "article.published": "Published",
  "article.updated": "Updated",

  "feed.searchPlaceholder": "Search news...",
  "feed.showing": "Showing {count} articles",
  "feed.scrollForMore": "Scroll for more",
  "feed.loadingMore": "Loading more articles...",
  "feed.loadMore": "Load more",
  "feed.noArticles": "No articles found",
  "feed.emptyHint": "Try changing your search or filter criteria, or refresh to try again.",
  "feed.refresh": "Refresh",
  "feed.noMatchHint": "Try adjusting your search or filters.",
  "feed.noMatch": "No articles match your filters",
  "feed.failed": "Failed to load articles",
  "feed.reload": "Reload page",
  "feed.sortBy": "Sort by",
  "feed.filters": "Filters",
  "feed.dateRange": "Date range",
  "sort.latest": "Latest",
  "sort.trending": "Trending",
  "sort.discussed": "Most discussed",
  "date.all": "All time",
  "date.today": "Today",
  "date.3days": "Last 3 days",
  "date.week": "This week",
  "date.2weeks": "Last 2 weeks",

  "notFound.title": "Page not found",
  "notFound.body": "The page you were looking for has moved or never existed.",
  "notFound.popular": "Popular pages",

  "ai.tldr": "TLDR",
  "ai.keyPoints": "Key Points",
  "ai.simplified": "Simplified",
  "ai.challengeThis": "Challenge This",
  "ai.marketSentiment": "Market Sentiment",
  "ai.whatThisMeans": "What This Means",
  "ai.keyFactors": "Key Factors",
  "article.rewrittenPrefix": "Rewritten by Byteline AI from the original",
  "article.rewrittenSuffix": "report.",
} as const;

/** Subject labels shown on chips and badges. Unknown values stay as they are. */
export const CATEGORY_LABELS: Record<string, { no: string; "zh-Hant": string }> = {
  All: { no: "Alle", "zh-Hant": "全部" },
  AI: { no: "KI", "zh-Hant": "人工智能" },
  Semiconductors: { no: "Halvledere", "zh-Hant": "半導體" },
  "Big Tech": { no: "Teknologigigantene", "zh-Hant": "科技巨頭" },
  "Startups & VC": { no: "Oppstart og venturekapital", "zh-Hant": "初創與創投" },
  Markets: { no: "Markeder", "zh-Hant": "市場" },
  Economy: { no: "Økonomi", "zh-Hant": "經濟" },
  Crypto: { no: "Krypto", "zh-Hant": "加密貨幣" },
  Cryptocurrency: { no: "Krypto", "zh-Hant": "加密貨幣" },
  Policy: { no: "Politikk", "zh-Hant": "政策" },
  Robotics: { no: "Robotikk", "zh-Hant": "機械人" },
  Space: { no: "Romfart", "zh-Hant": "太空" },
  Biotech: { no: "Bioteknologi", "zh-Hant": "生物科技" },
  Cybersecurity: { no: "Cybersikkerhet", "zh-Hant": "網絡安全" },
  Security: { no: "Sikkerhet", "zh-Hant": "安全" },
  Technology: { no: "Teknologi", "zh-Hant": "科技" },
  Business: { no: "Næringsliv", "zh-Hant": "商業" },
  Energy: { no: "Energi", "zh-Hant": "能源" },
  Gaming: { no: "Spill", "zh-Hant": "遊戲" },
  "EVs and Transportation": { no: "Elbiler og transport", "zh-Hant": "電動車與交通" },
  "Press Release": { no: "Pressemelding", "zh-Hant": "新聞稿" },
  "Latest News": { no: "Siste nytt", "zh-Hant": "最新消息" },
};

export const en = { ...enBase, ...enAuto };

export type MessageKey = keyof typeof en;
type Dictionary = Partial<Record<MessageKey, string>>;

const no: Dictionary = {
  "article.faqTitle": "Ofte stilte spørsmål",
  "nav.news": "Nyheter",
  "nav.weekly": "Ukens brief",
  "nav.podcasts": "Podkaster",
  "nav.social": "Sosialt",
  "nav.feed": "Strøm",
  "nav.markets": "Markeder",
  "nav.predictionMarkets": "Prediksjonsmarkeder",
  "nav.intelligence": "Intelligence Pro",
  "nav.leaderboard": "Topplisten",
  "nav.learn": "Lær",
  "nav.games": "Spill",
  "nav.bookmarks": "Bokmerker",
  "nav.search": "Søk",
  "nav.profile": "Profil",
  "nav.signIn": "Logg inn",
  "nav.about": "Om oss",
  "nav.editorialStandards": "Redaksjonelle retningslinjer",
  "nav.privacy": "Personvern",
  "nav.terms": "Bruksvilkår",

  "common.readMore": "Les mer",
  "common.loading": "Laster…",
  "common.tryAgain": "Prøv igjen",
  "common.share": "Del",
  "common.save": "Lagre",
  "common.language": "Språk",
  "common.backHome": "Tilbake til forsiden",
  "common.minRead": "min lesing",
  "common.translated": "Maskinoversatt",
  "common.translating": "Oversetter…",
  "common.readOriginal": "Les originalen på engelsk",

  "home.title": "Byteline: Teknologinyheter og markedsinnsikt",
  "home.subtitle":
    "Daglig kuratert dekning av banebrytende teknologi – fra KI og halvledere til romfart, robotikk, energi og markeder.",
  "home.latest": "Siste saker",
  "home.dailyRecap": "Dagens oppsummering",
  "home.topics": "Temaer",

  "article.notFound": "Fant ikke artikkelen",
  "article.relatedTitle": "Relaterte saker",
  "article.published": "Publisert",
  "article.updated": "Oppdatert",

  "feed.searchPlaceholder": "Søk i nyheter …",
  "feed.showing": "Viser {count} saker",
  "feed.scrollForMore": "Bla for mer",
  "feed.loadingMore": "Laster flere saker …",
  "feed.loadMore": "Last inn flere",
  "feed.noArticles": "Fant ingen saker",
  "feed.emptyHint": "Prøv å endre søket eller filtrene, eller last inn siden på nytt.",
  "feed.refresh": "Last inn på nytt",
  "feed.noMatchHint": "Prøv å justere søket eller filtrene.",
  "feed.noMatch": "Ingen saker treffer filtrene dine",
  "feed.failed": "Klarte ikke å laste sakene",
  "feed.reload": "Last siden på nytt",
  "feed.sortBy": "Sorter etter",
  "feed.filters": "Filtre",
  "feed.dateRange": "Tidsrom",
  "sort.latest": "Nyeste",
  "sort.trending": "Mest lest",
  "sort.discussed": "Mest omtalt",
  "date.all": "Hele tiden",
  "date.today": "I dag",
  "date.3days": "Siste 3 dager",
  "date.week": "Denne uken",
  "date.2weeks": "Siste 2 uker",

  "notFound.title": "Siden finnes ikke",
  "notFound.body": "Siden du lette etter er flyttet eller har aldri eksistert.",
  "notFound.popular": "Populære sider",

  "ai.tldr": "Kort fortalt",
  "ai.keyPoints": "Hovedpunkter",
  "ai.simplified": "Forenklet",
  "ai.challengeThis": "Motargumenter",
  "ai.marketSentiment": "Markedsstemning",
  "ai.whatThisMeans": "Hva dette betyr",
  "ai.keyFactors": "Nøkkelfaktorer",
  "article.rewrittenPrefix": "Skrevet om av Byteline AI fra den opprinnelige saken hos",
  "article.rewrittenSuffix": ".",
};

const zhHant: Dictionary = {
  "article.faqTitle": "常見問題",
  "nav.news": "新聞",
  "nav.weekly": "每週簡報",
  "nav.podcasts": "播客",
  "nav.social": "社群",
  "nav.feed": "動態",
  "nav.markets": "市場",
  "nav.predictionMarkets": "預測市場",
  "nav.intelligence": "Intelligence Pro",
  "nav.leaderboard": "排行榜",
  "nav.learn": "學習",
  "nav.games": "遊戲",
  "nav.bookmarks": "書籤",
  "nav.search": "搜尋",
  "nav.profile": "個人檔案",
  "nav.signIn": "登入",
  "nav.about": "關於我們",
  "nav.editorialStandards": "編輯準則",
  "nav.privacy": "私隱政策",
  "nav.terms": "使用條款",

  "common.readMore": "閱讀更多",
  "common.loading": "載入中…",
  "common.tryAgain": "再試一次",
  "common.share": "分享",
  "common.save": "儲存",
  "common.language": "語言",
  "common.backHome": "返回首頁",
  "common.minRead": "分鐘閱讀",
  "common.translated": "機器翻譯",
  "common.translating": "翻譯中…",
  "common.readOriginal": "閱讀英文原文",

  "home.title": "Byteline：科技新聞與市場情報",
  "home.subtitle": "每日精選顛覆性科技報道，涵蓋人工智能、半導體、太空、機械人、能源與金融市場。",
  "home.latest": "最新報道",
  "home.dailyRecap": "每日摘要",
  "home.topics": "主題",

  "article.notFound": "找不到這篇文章",
  "article.relatedTitle": "相關報道",
  "article.published": "發佈於",
  "article.updated": "更新於",

  "feed.searchPlaceholder": "搜尋新聞…",
  "feed.showing": "顯示 {count} 則報道",
  "feed.scrollForMore": "向下捲動載入更多",
  "feed.loadingMore": "正在載入更多報道…",
  "feed.loadMore": "載入更多",
  "feed.noArticles": "找不到報道",
  "feed.emptyHint": "請嘗試更改搜尋或篩選條件，或重新載入頁面。",
  "feed.refresh": "重新載入",
  "feed.noMatchHint": "請嘗試調整搜尋或篩選條件。",
  "feed.noMatch": "沒有符合篩選條件的報道",
  "feed.failed": "無法載入報道",
  "feed.reload": "重新載入頁面",
  "feed.sortBy": "排序方式",
  "feed.filters": "篩選",
  "feed.dateRange": "時間範圍",
  "sort.latest": "最新",
  "sort.trending": "熱門",
  "sort.discussed": "最多討論",
  "date.all": "全部時間",
  "date.today": "今天",
  "date.3days": "最近 3 天",
  "date.week": "本週",
  "date.2weeks": "最近 2 週",

  "notFound.title": "找不到頁面",
  "notFound.body": "您要找的頁面已移除或從未存在。",
  "notFound.popular": "熱門頁面",

  "ai.tldr": "重點摘要",
  "ai.keyPoints": "重點整理",
  "ai.simplified": "白話解說",
  "ai.challengeThis": "反面觀點",
  "ai.marketSentiment": "市場情緒",
  "ai.whatThisMeans": "這代表什麼",
  "ai.keyFactors": "關鍵因素",
  "article.rewrittenPrefix": "本文由 Byteline AI 改寫自",
  "article.rewrittenSuffix": "的原始報導。",
};

export const DICTIONARIES: Record<Locale, Dictionary> = {
  en,
  no: { ...no, ...noAuto },
  "zh-Hant": { ...zhHant, ...zhHantAuto },
};

export function translate(locale: Locale, key: MessageKey): string {
  return DICTIONARIES[locale]?.[key] ?? en[key];
}
